import {
  Box,
  Typography,
  Divider,
  SxProps,
  Theme,
  Tooltip,
} from "@mui/material";
import { useState, useEffect, useRef } from "react";
import { XCard } from "../../../../components/xCard/xCard";
import { IconButton } from "@mui/material";
import { Delete2Icon, ZapIcon } from "../../../../shared/images/icons";
import { textSM, textXS } from "../../../../shared/constants";
import colors from "../../../../shared/constants/color";
import { Button } from "../../../../components";
import { Plus, RefreshCcw, Save } from "react-feather";
import { AIScoreCircle } from "../shared/AIScoreCircle";
import { WorkingModelTag } from "../shared/WorkingModelTag";
import AILoading from "../../../../components/loading/Loading";
import type { AIPositionInfo } from "../../../interviewAssistant/InterviewAssistant";
import HeightIcon from "@mui/icons-material/Height";
import { useWiserTheme } from "../../../../hooks/useWiserTheme";
import {
  getAiRecommendedRootSx,
  getResizeHandleSx,
  getResizeHandleIconSx,
  aiRecommendedCardSx,
  aiRecommendedLoadingSx,
  centeredContentSx,
  columnGap12Sx,
  rowAlignCenterGap8Sx,
  cursorHelpSx,
  aiPositionRowContentSx,
  flexShrink0Sx,
  getAiPositionDividerSx,
} from "../../style";

interface IProps {
  positions: AIPositionInfo[] | null;
  onAddNew: () => void;
  onDelete: (id: string) => void;
  onGetNewSuggestions: () => void;
  onSaveAll?: () => void;
  onPositionClick?: (positionId: string) => void;
  loading: boolean;
  sx?: SxProps<Theme>;
}

export const AIRecommendedPositionsCard = ({
  positions,
  onAddNew,
  onDelete,
  onGetNewSuggestions,
  onSaveAll,
  onPositionClick,
  loading = false,
  sx,
}: IProps) => {
  const { darkModeActive, tokens } = useWiserTheme();
  const [height, setHeight] = useState<number>(window.innerHeight * 0.3);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const startYRef = useRef<number>(0);
  const startHeightRef = useRef<number>(0);

  const handleMouseDown = (e: React.MouseEvent) => {
    console.log("Mouse down - starting drag");
    setIsDragging(true);
    startYRef.current = e.clientY;
    startHeightRef.current = height;
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        console.log("Mouse moving, dragging:", isDragging);
        const deltaY = startYRef.current - e.clientY;
        const minHeight = window.innerHeight * 0.2;
        const maxHeight = window.innerHeight * 0.7;
        const newHeight = Math.max(
          minHeight,
          Math.min(maxHeight, startHeightRef.current + deltaY),
        );
        console.log("New height:", newHeight);
        setHeight(newHeight);
      }
    };

    const handleMouseUp = () => {
      console.log("Mouse up - ending drag");
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  return (
    <Box
      sx={getAiRecommendedRootSx(
        height,
        sx && typeof sx === "object"
          ? (sx as { minWidth?: string | number }).minWidth
          : undefined,
      )}
    >
      <Box
        onMouseDown={handleMouseDown}
        sx={getResizeHandleSx(isDragging, darkModeActive, tokens, "ai")}
      >
        <HeightIcon sx={getResizeHandleIconSx(darkModeActive, tokens, "ai")} />
      </Box>
      <XCard
        title="AI Önerilen Pozisyonlar"
        titleStyle="aiGradient"
        icon={<img src={ZapIcon} />}
        style="aiNeutral"
        headerAction={
          <Button
            hasIcon
            iconSize="md"
            iconSpace="8px"
            variant="texted"
            size="md"
            color={colors.blue.B300}
            fontWeight="500"
            onClick={onGetNewSuggestions}
            disabled={loading}
          >
            <RefreshCcw size={16} color={colors.blue.B300} />
            Yenile
          </Button>
        }
        footerAction={
          <>
            <Button
              hasIcon
              iconSize="md"
              iconSpace="8px"
              variant="texted"
              size="md"
              color={colors.blue.B300}
              fontWeight="500"
              onClick={onAddNew}
              disabled={loading}
            >
              <Plus size={16} color={colors.blue.B300} />
              Listeye Ekle
            </Button>
            <Button
              hasIcon
              iconSize="md"
              iconSpace="8px"
              variant="texted"
              size="md"
              color={colors.blue.B300}
              fontWeight="500"
              onClick={onSaveAll}
              disabled={loading}
            >
              <Save size={16} color={colors.blue.B300} />
              Listeyi Kaydet
            </Button>
          </>
        }
        sx={aiRecommendedCardSx}
      >
        {loading ? (
          <Box sx={aiRecommendedLoadingSx}>
            <AILoading />
          </Box>
        ) : !positions || positions.length === 0 ? (
          <Box sx={centeredContentSx}>
            <Typography
              sx={textXS(
                400,
                darkModeActive ? tokens.textMuted : colors.neutral.N500,
              )}
            >
              {positions
                ? "Listelenecek pozisyon bulunamadı."
                : "Yeterli veri olmadığı için AI pozisyon önerileri oluşturulamadı."}
            </Typography>
          </Box>
        ) : (
          <Box sx={columnGap12Sx}>
            {positions.map((position, index) => (
              <Box key={position.positionId}>
                <Box sx={rowAlignCenterGap8Sx}>
                  {/* Score Circle with Progress */}
                  <Box sx={cursorHelpSx}>
                    <AIScoreCircle
                      score={position.matchScore}
                      uniqueId={position.positionId.toString()}
                    />
                  </Box>

                  {/* Content */}
                  <Box
                    sx={aiPositionRowContentSx}
                    onClick={() => onPositionClick?.(position.positionId)}
                  >
                    <Box sx={rowAlignCenterGap8Sx}>
                      <Typography
                        sx={textSM(
                          500,
                          darkModeActive ? tokens.accent : colors.blue.B300,
                        )}
                      >
                        {position.techGroupNames.join(" - ") || ""}
                      </Typography>
                      <WorkingModelTag
                        workingModel={position.workingModel || ""}
                      />
                    </Box>
                    <Typography
                      sx={textXS(
                        400,
                        darkModeActive
                          ? tokens.textSecondary
                          : colors.neutral.N500,
                      )}
                    >
                      {position.client || ""} · {position.team || ""}
                    </Typography>
                  </Box>

                  {/* Delete Icon */}
                  <IconButton
                    size="small"
                    sx={flexShrink0Sx}
                    onClick={() => onDelete(position.positionId)}
                  >
                    <img src={Delete2Icon} />
                  </IconButton>
                </Box>
                {/* Divider - son pozisyon hariç */}
                {index < positions.length - 1 && (
                  <Divider sx={getAiPositionDividerSx(darkModeActive, tokens)} />
                )}
              </Box>
            ))}
          </Box>
        )}
      </XCard>
    </Box>
  );
};
